import type { InvitationSignInOptionsResponse } from "@repo/app-types/api/user";
import axios from "axios";
import { apiBaseUrl } from "./shared.js";

/**
 * Pre-auth lookup of the auth methods the invited company accepts, keyed off the
 * `/accept-invitation?t=<token>` token. Uses a bare axios GET (no auth header)
 * against the public endpoint, since RTK Query's base query requires a session
 * that doesn't exist yet — the same pattern as the sign-in page's
 * `/domain-login-status` lookup.
 */
export async function fetchInvitationSignInOptions(
  token: string,
): Promise<InvitationSignInOptionsResponse> {
  const url = new URL("/invitation-sign-in-options", apiBaseUrl);
  url.searchParams.set("t", token);
  const { data } = await axios.get<InvitationSignInOptionsResponse>(
    url.toString(),
  );
  return data;
}

/**
 * Asks the server to email the invitation magic link to the invited recipient,
 * keyed off the `/accept-invitation?t=<token>` token. The send happens
 * server-side so the invitee's full email never reaches this unauthenticated
 * client and the link can only go to the invited address. Bare axios POST (no
 * auth header), matching {@link fetchInvitationSignInOptions}.
 */
export async function sendInvitationMagicLink(
  token: string,
): Promise<{ error: string | null }> {
  const url = new URL("/invitation-sign-in-options/magic-link", apiBaseUrl);
  try {
    await axios.post(url.toString(), { t: token });
    return { error: null };
  } catch {
    return { error: "An error occurred sending your link. Please try again." };
  }
}

/**
 * Whether an error from {@link fetchInvitationSignInOptions} is a definitive
 * "invitation invalid/expired" response (a 4xx), as opposed to a transient
 * network/server error where the caller should fall back to standard sign-in.
 */
export function isInvalidInvitationError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response !== undefined &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
}
